import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection, doc, getDoc, getDocs, query, where, setDoc, Timestamp
} from 'firebase/firestore';
import { firestore, storage } from '../views/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import '../components/CreatePostModal.css';

const CATEGORIES = [
  'Pool/Billiards',
  'Quick Race / Match',
  'Tournament',
  'League Game',
  'Team / Practice',
  'Food',
  'Job/Gig',
  'Having Fun!',
  'Gaming',
  'Film',
  'Tech',
  'Art',
  'Live Music',
  'Basketball',
  'Football',
  'Soccer',
  'Baseball',
  'Cars',
  'LifeStyle',
  'Travel',
  'Other',
];

const RACE_TEMPLATES = [
  'Looking for a race today',
  'Practice race results',
  'Tournament warmup',
  'League night update',
];

export default function CreatePostModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const uid = user?.uid;

  const [profile, setProfile] = useState(null);
  const [hasBiz, setHasBiz] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selLoc, setSelLoc] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [category, setCategory] = useState('Pool/Billiards');
  const [tagsText, setTagsText] = useState('');
  const [postAsBiz, setPostAsBiz] = useState(false);
  const [urls, setUrls] = useState(['', '', '']);
  const [files, setFiles] = useState([null, null, null]);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState('');
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const minTitle = 5;
  const minText = 10;

  useEffect(() => {
    if (!uid) return;
    (async () => {
      const uSnap = await getDoc(doc(firestore, 'userprofiles', uid));
      if (uSnap.exists()) setProfile(uSnap.data());

      const bQ = query(
        collection(firestore, 'businessprofiles'),
        where('businessId', '==', uid),
        where('businessStatus', '==', 'active')
      );
      const bS = await getDocs(bQ);
      if (!bS.empty) {
        const locs = bS.docs.map(d => ({ id: d.id, name: d.data().businessBrand || d.data().businessName || 'Business location', ...d.data() }));
        setLocations(locs);
        setSelLoc(locs[0].id);
        setHasBiz(true);
      }
    })();
  }, [uid]);

  const tags = useMemo(() => tagsText
    .split(',')
    .map(item => item.trim().replace(/^#/, ''))
    .filter(Boolean)
    .slice(0, 6), [tagsText]);

  const disabled =
    !uid ||
    title.trim().length < minTitle ||
    text.trim().length < minText ||
    !category ||
    (postAsBiz && !selLoc) ||
    saving;

  const handleFile = (i, file) => {
    if (!file) return;
    const arr = [...files];
    arr[i] = file;
    setFiles(arr);
  };

  const removeSlot = i => {
    const newFiles = [...files];
    const newUrls = [...urls];
    newFiles[i] = null;
    newUrls[i] = '';
    setFiles(newFiles);
    setUrls(newUrls);
  };

  const uploadAll = async () => {
    const uploadedUrls = [...urls];
    for (let i = 0; i < 3; i++) {
      if (files[i]) {
        const storageRef = ref(storage, `feedMedia/${uid}/${Date.now()}_${i}_${files[i].name}`);
        const snap = await uploadBytes(storageRef, files[i]);
        uploadedUrls[i] = await getDownloadURL(snap.ref);
      }
    }
    return uploadedUrls;
  };

  const applyTemplate = (template) => {
    if (!title.trim()) setTitle(template);
    if (!text.trim()) {
      setText('Game: 8-Ball / 9-Ball\nRace to: 5\nLocation: \nLooking for: practice, action, or friendly match');
    }
    setCategory('Quick Race / Match');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setSaving(true);
    setError('');

    try {
      const finalUrls = await uploadAll();
      const newRef = doc(collection(firestore, 'feedPost'));
      const postId = newRef.id;

      let identity = profile || {};
      if (postAsBiz && selLoc) {
        const selected = locations.find(item => item.id === selLoc);
        const locSnap = await getDoc(doc(firestore, 'businessprofiles', selLoc));
        identity = locSnap.exists() ? locSnap.data() : selected || {};
      }

      const nickname = postAsBiz
        ? identity?.businessBrand || identity?.businessName || 'Pool 4 Real Business'
        : identity?.nickname || user.displayName || 'Pool Player';
      const userImage = identity?.profileImageUrl || identity?.businessImageUrl || identity?.imageUrl || '';

      const docData = {
        feedPostId: postId,
        userId: uid,
        postTitle: title.trim(),
        postText: text.trim(),
        image1: finalUrls[0] || '',
        image2: finalUrls[1] || '',
        image3: finalUrls[2] || '',
        timestamp: Timestamp.now(),
        status: 'inProcess',
        category,
        tags,
        nickname,
        userImage,
        isBusinessPost: postAsBiz,
        postlikes: [],
        postdislikes: [],
        views: 0,
        commentCount: 0,
      };

      if (postAsBiz && selLoc) {
        docData.businessId = uid;
        docData.locationId = selLoc;
      }

      await setDoc(newRef, docData);
      setShowToast(true);
      onCreated?.();
      setTimeout(() => {
        setShowToast(false);
        onClose();
      }, 1200);
    } catch (err) {
      console.error('Error creating post:', err);
      setError(err.message || 'Unable to create post right now.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        show={true}
        onHide={onClose}
        size="lg"
        centered
        scrollable
        keyboard={false}
        backdropClassName="create-post-backdrop"
        dialogClassName="create-post-dialog modal-dialog-scrollable"
      >
        <Modal.Header closeButton>
          <Modal.Title>New Pockit</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="create-post-quickbar">
            <button type="button" onClick={() => setTemplatesOpen(v => !v)}>
              Race templates
            </button>
          </div>

          {templatesOpen && (
            <div className="create-post-template-row">
              {RACE_TEMPLATES.map(template => (
                <button type="button" key={template} onClick={() => applyTemplate(template)}>
                  {template}
                </button>
              ))}
            </div>
          )}

          <div className="create-post-modal-images">
            {[0, 1, 2].map(i => (
              <div key={i} className="create-post-modal-slot">
                {(urls[i] || files[i]) ? (
                  <div className="create-post-modal-thumb-wrap">
                    <img src={urls[i] || URL.createObjectURL(files[i])} alt="Preview" />
                    <button type="button" className="create-post-modal-remove" onClick={() => removeSlot(i)}>×</button>
                  </div>
                ) : (
                  <label className="create-post-modal-add">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => handleFile(i, e.target.files?.[0])}
                    />
                    <span>＋</span>
                    <small>Optional photo</small>
                  </label>
                )}
              </div>
            ))}
          </div>

          <Form className="create-post-form" onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                maxLength={100}
                placeholder="Looking for a 9-ball race tonight"
              />
              <Form.Text className={title.trim().length < minTitle ? 'text-danger' : 'text-muted'}>
                {title.trim().length < minTitle ? `Minimum ${minTitle} characters` : `${100 - title.length} left`}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={text}
                onChange={e => setText(e.target.value)}
                maxLength={900}
                placeholder="City, venue, skill level, race format, or update..."
              />
              <Form.Text className={text.trim().length < minText ? 'text-danger' : 'text-muted'}>
                {text.trim().length < minText ? `Minimum ${minText} characters` : `${900 - text.length} left`}
              </Form.Text>
            </Form.Group>

            <Row className="mb-3 gx-2 gy-2">
              <Col md={6}>
                <Form.Label>Category</Form.Label>
                <Form.Select value={category} onChange={e => setCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Tags</Form.Label>
                <Form.Control
                  value={tagsText}
                  onChange={e => setTagsText(e.target.value)}
                  maxLength={120}
                  placeholder="8ball, sanjuan, practice"
                />
              </Col>
            </Row>

            {hasBiz && (
              <div className="create-post-business-box">
                <Form.Check
                  type="switch"
                  id="bizSwitch"
                  label="Post as one of my business locations"
                  checked={postAsBiz}
                  onChange={() => setPostAsBiz(b => !b)}
                />

                {postAsBiz && (
                  <Form.Group className="mt-3">
                    <Form.Label>Business location</Form.Label>
                    <Form.Select value={selLoc} onChange={e => setSelLoc(e.target.value)}>
                      <option value="">Choose a location</option>
                      {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                )}
              </div>
            )}

            {error && <div className="create-post-error">{error}</div>}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={disabled}>
            {saving ? 'Submitting…' : 'Submit for review'}
          </Button>
        </Modal.Footer>
      </Modal>

      {showToast && <div className="create-post-modal-toast">✅ Your Pockit is in review!</div>}
    </>
  );
}
